import { useEffect, useState } from 'react';
import type { ISsoLoginListItem } from '@skystone/shared';
import { userApi } from '@/services/user-api';
import { getErrorMessage } from '@/lib/utils';
import { Badge, ErrorBanner, Spinner } from '@/components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatDateTime } from '@/lib/date-utils';

interface SsoLoginsTabProps {
  /**
   * Open the People tab's detail dialog for the given Person. Provided by
   * AdminPeoplePage so the linked-person link can navigate users without
   * SsoLoginsTab needing its own dialog state.
   */
  onOpenPersonDetail: (personId: string) => void;
}

/**
 * Read-only listing of every row in the local `users` table (PRODOPSAI-450 /
 * Epic PRODOPSAI-444 US6). Surfaces who has actually logged into skystone
 * — separate from the Entra directory truth shown on the People tab.
 *
 * No edit / link / unlink actions here. The linked-person link opens the
 * existing PersonDetailDialog via AdminPeoplePage's handler.
 */
export function SsoLoginsTab({ onOpenPersonDetail }: Readonly<SsoLoginsTabProps>) {
  const [rows, setRows] = useState<ISsoLoginListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    userApi.listSsoLogins()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load SSO logins'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          SSO logins {rows !== null && `(${rows.length})`}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Every account that has logged into skystone. Read-only — Person matching is driven by
          the Entra sync on the People tab.
        </p>
      </CardHeader>
      <CardContent>
        {error !== null && <ErrorBanner message={error} />}
        {loading && rows === null && <Spinner />}
        {rows !== null && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No SSO logins recorded yet.
          </p>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Username</th>
                  <th className="px-4 py-2 font-medium">SSO email</th>
                  <th className="px-4 py-2 font-medium">Auth method</th>
                  <th className="px-4 py-2 font-medium">Linked person</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // Bind once so the click handler's closure doesn't lose the
                  // null-narrowed type from the outer guard.
                  const linkedPerson = row.linkedPerson;
                  return (
                    <tr key={row.id} className="border-t">
                      <td className="px-4 py-2 font-medium text-foreground">{row.username}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.ssoEmail ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Badge variant="secondary">
                          {row.authMethod === 'sso' ? 'SSO' : 'Local'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        {linkedPerson === null ? (
                          <span className="text-muted-foreground italic">Not matched</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onOpenPersonDetail(linkedPerson.id)}
                            className="text-primary hover:underline"
                          >
                            {linkedPerson.displayName}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {formatDateTime(row.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
